<template>
    <RectangleTile @click="handleCardClick" v-interact class="background-tile-primary" v-if="offerAndItsProgress && offerAndItsProgress.offer">

        <template v-slot:icon>
            <TileIcon>
                <v-img v-if="offerAndItsProgress.offer && offerAndItsProgress.offer.icon" :src="offerAndItsProgress.offer.icon"></v-img>
            </TileIcon>
        </template>

        <template v-slot:details>
            <TileDetails>
                <template v-slot:name>{{ offerAndItsProgress.offer ? offerAndItsProgress.offer.name : '' }}</template>
                <template v-slot:description>{{ offerAndItsProgress.offer ? offerAndItsProgress.offer.instructions : '' }}</template>
            </TileDetails>
        </template>

        <template v-slot:actions>
            <TileCoins
                v-if="offerAndItsProgress.offer"
                :coins_amount="offerAndItsProgress.offer.coins_amount"
                :offer-platforms="offerAndItsProgress.offer.platforms"
                :allignment="TILE_ACTION_ALIGNMENT.HORIZONTAL"
                :show-boost="true"
            />
        </template>
        
        <template v-slot:append-bottom>
            <v-divider class="my-2 w-100"></v-divider>
            <v-row no-gutters>
                <v-col cols="12" class="d-flex flex-row justify-space-between mb-2">
                    <span class="text-body-2">Status</span>
                    <span class="text-body-2" v-if="offerAndItsProgress.progress_info && offerAndItsProgress.progress_info.last_started_at">
                        {{ (new Date(offerAndItsProgress.progress_info.last_started_at * 1000)).toDateString() }}
                    </span>
                </v-col>
            </v-row>

            <v-progress-linear class="progress-bar" height="25" :value="progressPercentage" rounded>
                <template #default>
                    <span class="progress-label weight-700 text-12">
                        Progress - {{ completedSteps }}/{{ totalSteps }}
                    </span>
                </template>
            </v-progress-linear>
        </template>
    </RectangleTile>
</template>



<script setup lang="ts">

import { computed } from 'vue';
import TileIcon from "@/components/Common/Tile-Icon.vue";
import TileCoins from "@/components/Common/Tile-Coins.vue";
import { OfferStatusStore } from '@/store/OfferStatusStore';
import TileDetails from '@/components/Common/Tile-Details.vue';
import { OfferProgress } from '@/interfaces/Offers/OfferProgress';
import RectangleTile from '@/components/Tiles/Rectangle-Tile.vue';
import { TILE_ACTION_ALIGNMENT } from "@/constants/TileActionAllignment";

const props = defineProps({
    id: Number,
    offer: Object as () => OfferProgress,
});

const emit = defineEmits(['cardClicked']);

const offerStatusStore = OfferStatusStore();

const offerAndItsProgress = computed(() => {
    // First check if the offer is passed as a prop
    if (props.offer) return props.offer;
    
    // Then check if we can get it from the store
    if (offerStatusStore.offersInProgress && props.id !== undefined && props.id !== null) {
        return offerStatusStore.offersInProgress[props.id] || null;
    }
    
    // Return a default empty object structure to prevent errors
    return {
        offer: null,
        progress_info: null
    };
});

const handleCardClick = () => {
    // Only set the selected offer if it exists
    if (offerAndItsProgress.value && offerAndItsProgress.value.offer) {
        offerStatusStore.setSelectedOffer(offerAndItsProgress.value);
        emit('cardClicked', props.offer);
    }
};


const totalSteps = computed(() => {
    // Add null check to prevent errors
    if (!offerAndItsProgress.value || !offerAndItsProgress.value.offer || !offerAndItsProgress.value.offer.steps) {
        return 0;
    }
    return offerAndItsProgress.value.offer.steps.length;
});

const completedSteps = computed(() => {
    // Add null check to prevent errors
    if (!offerAndItsProgress.value || !offerAndItsProgress.value.offer || !offerAndItsProgress.value.offer.steps) {
        return 0;
    }
    return offerAndItsProgress.value.offer.steps.filter(step => step.completed).length;
});

const progressPercentage = computed(() => {
    if (totalSteps.value === 0) return 0;
    return (completedSteps.value / totalSteps.value) * 100;
});

</script>
